/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { useMemo } from 'react';
import { Typography, Box, Grid } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { FlexBox } from 'components/Containers/FlexBox';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  cleanDTCString,
  processDailyChartData,
  processDailyDtcCodeData,
  getLimitedTypes,
} from './utils';
import useTimeDisplayMode from 'hooks/useTimeDisplayMode';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

export const DailyDTCChart = ({
  enableDarkTheme,
  data,
  selectedAsset,
  selectedType,
  colorMap,
  maxItems = 50,
}: {
  enableDarkTheme: boolean;
  data: any[];
  selectedAsset: string | null;
  setSelectedAsset: any;
  selectedType: string | null;
  setSelectedType: any;
  colorMap: any;
  maxItems?: number;
}) => {
  const { t } = useTranslation();
  const { preferredTimeDisplayMode, timezone } = useTimeDisplayMode();

  const filteredData = useMemo(() => {
    let result = data;
    if (selectedAsset) {
      result = result.filter((item) => item.asset === selectedAsset);
    }
    if (selectedType) {
      result = result.filter((item) => item.dtc_type === selectedType);
    }
    return result;
  }, [data, selectedAsset, selectedType]);

  const allDtcTypes = useMemo(
    () => [...new Set(filteredData.map((item) => item.dtc_type))],
    [filteredData]
  );

  const allDtcCodes = useMemo(
    () => [...new Set(filteredData.map((item) => item.dtc_code))],
    [filteredData]
  );

  const dtcTypes = useMemo(
    () => getLimitedTypes(allDtcTypes, filteredData, 10),
    [allDtcTypes, filteredData]
  );

  const dtcCodes = useMemo(
    () => getLimitedTypes(allDtcCodes, filteredData, 10),
    [allDtcCodes, filteredData]
  );

  const chartData = useMemo(() => {
    if (selectedType) {
      return processDailyDtcCodeData(
        filteredData,
        dtcCodes,
        preferredTimeDisplayMode,
        timezone,
        maxItems
      );
    }

    return processDailyChartData(
      filteredData,
      dtcTypes,
      preferredTimeDisplayMode,
      timezone,
      maxItems
    );
  }, [
    filteredData,
    selectedType,
    dtcTypes,
    dtcCodes,
    preferredTimeDisplayMode,
    timezone,
    maxItems,
  ]);

  const textColor = (enableDarkTheme ? _palette.dark : _palette.light)
    .textPrimary;

  const isWeeklyAggregation =
    chartData.length > 0 && chartData[0].name?.includes('Week of');

  const showEmptyState = selectedAsset
    ? dtcCodes.length === 0
    : dtcTypes.length === 0;

  return (
    <Grid item lg={7} md={12} sm={12} xs={12}>
      <Box
        sx={{
          marginTop: '20px',
          border: `1px solid ${
            enableDarkTheme
              ? _palette.dark.borderGrey
              : _palette.light.borderGrey
          }`,
          borderRadius: '4px',
          backgroundColor: enableDarkTheme
            ? _palette.dark.boxBackground
            : _palette.light.boxBackground,
          padding: '10px',
          minHeight: '170px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              color: textColor,
              fontSize: '11px',
            }}
          >
            {selectedAsset ? (
              <>
                Daily{' '}
                {selectedType ? cleanDTCString(selectedType) : 'Sensor Type'}{' '}
                {t('distribution_for_asset_2')}{' '}
                <span
                  style={{
                    color: enableDarkTheme
                      ? _palette.dark.primary
                      : _palette.light.primary,
                    fontSize: '14px',
                  }}
                >
                  {selectedAsset}
                </span>
              </>
            ) : (
              t('daily_sensor_type_distribution')
            )}
          </Typography>
          {isWeeklyAggregation && (
            <Typography
              sx={{
                color: '#D9A92E',
                fontSize: '10px',
                fontStyle: 'italic',
              }}
            >
              {t('aggregated_by_week')}
            </Typography>
          )}
        </Box>

        {showEmptyState ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
            }}
          >
            <Typography
              sx={{
                color: textColor,
                fontSize: '14px',
                textAlign: 'center',
              }}
            >
              {t('no_data_available_for_the_selected_filters')}
            </Typography>
          </Box>
        ) : (
          <FlexBox
            sx={{
              maxWidth: 'inherit',
              paddingTop: '20px',
              marginRight: '10px',
              paddingBottom: '10px',
              height: '400px',
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={800}
                height={400}
                data={chartData}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 10,
                }}
              >
                <CartesianGrid
                  stroke={
                    (enableDarkTheme ? _palette.dark : _palette.light)
                      .cartesianGrid
                  }
                  horizontal={true}
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  textAnchor="end"
                  tick={{
                    fontSize: 11,
                    fill: textColor,
                  }}
                  angle={-65}
                  height={70}
                />
                <YAxis
                  tick={{
                    fontSize: 11,
                    fill: textColor,
                  }}
                  label={{
                    value: t('count_axis'),
                    angle: -90,
                    offset: 30,
                    position: 'insideLeft',
                    fill: textColor,
                    fontWeight: 'bold',
                    fontFamily: 'Arial',
                    fontSize: '12px',
                    style: {
                      textAnchor: 'middle',
                    },
                  }}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const filteredPayload = payload.filter(
                        (entry) => entry.value !== 0
                      );
                      if (filteredPayload.length === 0) return null;

                      return (
                        <div
                          style={{
                            backgroundColor: '#FFF',
                            opacity: 1,
                            border: `1px solid ${_palette.dark.primary}`,
                            padding: '10px',
                            fontSize: '10px',
                            borderRadius: '5px',
                          }}
                        >
                          <p>{`${
                            isWeeklyAggregation ? 'Week' : 'Date'
                          }: ${label}`}</p>
                          {filteredPayload.map((entry: any, index) => (
                            <p
                              key={`item-${index}`}
                              style={{ color: entry.color }}
                            >
                              {cleanDTCString(entry.name)}: {entry.value}
                            </p>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                  cursor={{
                    fill: enableDarkTheme
                      ? alpha(_palette.dark.primary, 0.15)
                      : 'rgba(204, 204, 204, 0.3)',
                  }}
                />
                <Legend
                  wrapperStyle={{
                    fontSize: '10px',
                    paddingBottom: '20px',
                  }}
                  iconSize={10}
                  verticalAlign="top"
                  iconType="circle"
                  align="left"
                  formatter={cleanDTCString}
                />
                {(selectedType ? dtcCodes : dtcTypes).map((type) => (
                  <Bar
                    key={type}
                    dataKey={type}
                    stackId="a"
                    fill={colorMap[type]}
                    name={type}
                    barSize={35}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </FlexBox>
        )}
      </Box>
    </Grid>
  );
};
